import { Component, OnInit, ViewChild } from '@angular/core';
import {
  collection,
  collectionData,
  doc,
  Firestore,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from '@angular/fire/firestore';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { AlertController, NavController, Platform, PopoverController } from '@ionic/angular';
import {
  CREATE_KB_ARTICLE_DRAFT_TOOL,
  CreateKbArticleDraftInput,
  NAVIGATE_ACTION_NAME,
} from '@unthread-io/copilot-frontend-tools';
import * as _ from 'lodash';
import moment from 'moment';
import { CONTEXTUAL_AUTHORIZATION_REQUIREMENTS } from 'src/constants/contextual-authorization.constants';
import { PERMISSIONS_V2_FEATURE_FLAG } from 'src/constants/feature-flags.constants';
import { environment } from 'src/environments/environment';
import { KnowledgeBaseArticle, Profile } from 'src/models';
import { AnalyticsService } from 'src/services/analytics.service';
import { AuthService } from 'src/services/auth.service';
import { ConversationsService } from 'src/services/conversations.service';
import { ContextualAuthorizationService } from 'src/services/contextual-authorization.service';
import { CopilotContextService } from 'src/services/copilot-context.service';
import { CopilotBackgroundChecksService } from 'src/services/copilot-background-checks.service';
import { CopilotNotificationsService } from 'src/services/copilot-notifications.service';
import { CustomCommandBarService } from 'src/services/custom-command-bar.service';
import { CustomersService } from 'src/services/customers.service';
import { EmbeddedAppConfigService } from 'src/services/embedded-app-config.service';
import { GlobalCreateService } from 'src/services/global-create.service';
import { KnowledgeBaseArticlesService } from 'src/services/knowledge-base-articles.service';
import { MessageService } from 'src/services/message.service';
import { UsersService } from 'src/services/users.service';

import { ActionDropdownComponent, ActionDropdownType } from './components/action-dropdown/action-dropdown.component';
import { CopilotPanelComponent } from './components/copilot-panel/copilot-panel.component';
import { ProjectSelectorComponent } from './components/project-selector/project-selector.component';
import { collections, routes, USER_NOTIFICATION_TYPES } from './constants';
import { NotificationsComponent } from './notifications/notifications.component';
import { ProfilePopoverPage } from './profile/profile-popover/profile-popover.page';
import { hexToRgb } from './utils/utils';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit {
  @ViewChild(CopilotPanelComponent) copilotPanel?: CopilotPanelComponent;

  profile: Profile;
  buttonsDisabled = false;
  isReady = false;
  notifications: any[] = [];
  unreadNotifications = 0;
  hideNavBar = false;
  trialStatus: string;
  trialExpired: boolean;
  isSuperAdmin = false;
  gitHash: string;
  showProjectSelector = true;
  showCopilot = false;
  readonly commandBarShortcut = navigator.platform.toUpperCase().includes('MAC') ? '⌘K' : 'Ctrl+K';
  private isReloadPromptShowing = false;

  get canQuickCompose(): boolean {
    if (!this.authService.user) {
      return false;
    }

    if (!this.authService.isFeatureEnabled(PERMISSIONS_V2_FEATURE_FLAG)) {
      return true;
    }

    const projectId = this.authService.isNewNavEnabled()
      ? this.authService.mostRecentProject?.id
      : this.authService.selectedProject?.id;

    return this.contextualAuthorizationService.isAuthorized(
      projectId ?? null,
      CONTEXTUAL_AUTHORIZATION_REQUIREMENTS.MANAGE_BROADCASTS,
    );
  }

  expandCopilot(): void {
    if (this.showCopilot) {
      return;
    }

    // The panel brings up any notifications waiting here once it is open.
    this.copilotContext.snapshotBeforeOpen();
    this.showCopilot = true;
  }

  private setupCopilotOpenListener(): void {
    this.copilotContext.registerOpenHandler((message) => {
      if (!this.showCopilot) {
        this.showCopilot = true;
      }

      // If there's a message, send it to the copilot panel after it renders
      if (message && this.copilotPanel) {
        setTimeout(() => {
          this.copilotPanel?.onSendMessage({ text: message });
        }, 0);
      }
    });
  }

  public environment = environment;
  public appPages = [];

  constructor(
    private platform: Platform,
    private router: Router,
    private route: ActivatedRoute,
    private afs: Firestore,
    private navController: NavController,
    private popoverCtrl: PopoverController,
    private msgSrvc: MessageService,
    private alertCtrl: AlertController,
    public authService: AuthService,
    private analyticsService: AnalyticsService,
    private globalCreateService: GlobalCreateService,
    private customCommandBarService: CustomCommandBarService,
    private customersService: CustomersService,
    private embeddedAppConfigService: EmbeddedAppConfigService,
    private conversationsService: ConversationsService,
    private contextualAuthorizationService: ContextualAuthorizationService,
    private copilotContext: CopilotContextService,
    public copilotNotifications: CopilotNotificationsService,
    private knowledgeBaseArticlesService: KnowledgeBaseArticlesService,
    private swUpdate: SwUpdate,
    copilotBackgroundChecks: CopilotBackgroundChecksService,
    private usersService: UsersService,
  ) {
    // Before any page registers, so every page's background check is picked up.
    copilotBackgroundChecks.start();
    this.appPages = [
      {
        title: 'Home',
        url: `/${routes.DASHBOARD}/${routes.HOME}`,
        icon: 'home',
      },
      {
        title: 'Inbox',
        url: `/${routes.DASHBOARD}/${routes.INBOX}`,
        icon: 'file-tray',
      },
    ];

    this.initializeApp();
    this.embeddedAppConfigService.applyDocumentState();
    this.hideNavBar = this.embeddedAppConfigService.shouldHideUnthreadTopNav;

    this.setupSwUpdates();

    this.setupCopilotOpenListener();

    // Register app-wide copilot actions on the root component so they're
    // available regardless of which page is active (and before Copilot is
    // opened). WebMCP's `navigation_assistant` and the BE Copilot's
    // `run_navigation_assistant` both dispatch through this action.
    this.copilotContext.registerAppAction(NAVIGATE_ACTION_NAME, (payload: { url?: string }) => {
      if (!payload?.url) {
        return false;
      }

      this.navController.navigateForward(payload.url);

      return true;
    });

    // KB article drafting is initiated from anywhere — the BE Copilot's
    // `draft_knowledge_base_article` tool emits `create_kb_article_draft`,
    // and the WebMCP wrapper of the same name dispatches into here too.
    this.copilotContext.registerAppAction(CREATE_KB_ARTICLE_DRAFT_TOOL.name, (payload: CreateKbArticleDraftInput) => {
      return this.createKbArticleDraft(payload);
    });

    this.authService.onAuthReady(() => {
      this.configureForTenant();
      this.subscribeToNotifications();
    });
  }

  private async createKbArticleDraft(payload: CreateKbArticleDraftInput): Promise<boolean> {
    const { articleId, projectId } = payload;

    const title = typeof payload.title === 'string' ? payload.title.trim() : '';
    const content = typeof payload.content === 'string' ? payload.content.trim() : '';
    const sourceConversationIds = Array.isArray(payload.sourceConversationIds) ? payload.sourceConversationIds : [];

    // Track whether the draft exists server-side so the catch block can
    // distinguish a creation failure from a navigation failure on a draft
    // that's already been persisted (when the BE passes us an articleId, the
    // draft has already been created — the only thing left for us to do is
    // open it).
    let draftPersisted = Boolean(articleId);

    try {
      const knowledgeBasePath = projectId ? `/dashboard/projects/${projectId}/knowledge` : '/dashboard/knowledge';

      if (articleId) {
        await this.navController.navigateForward(`${knowledgeBasePath}/articles/${articleId}/edit`);
        await this.msgSrvc.show('Draft article created.');

        return true;
      }

      if (!title || !content) {
        await this.msgSrvc.showError('Tailor could not create a draft article from the generated content.');

        return false;
      }

      const article = await this.knowledgeBaseArticlesService.create({
        title,
        content,
        status: 'draft',
        projectId,
        slackCxTicketId: sourceConversationIds.length === 1 ? sourceConversationIds[0] : undefined,
      } as Partial<KnowledgeBaseArticle> as KnowledgeBaseArticle);
      draftPersisted = true;

      await this.navController.navigateForward(`${knowledgeBasePath}/articles/${article.id}/edit`);
      await this.msgSrvc.show('Draft article created.');

      return true;
    } catch (error) {
      console.error('Failed to handle Tailor draft article:', error);
      await this.msgSrvc.showError(
        draftPersisted ? "Couldn't open the draft article." : 'Failed to create the draft article.',
      );

      return false;
    }
  }

  initializeApp() {
    const theme = localStorage.getItem('theme');

    if (theme === 'dark' || theme === 'dark-gray' || theme === 'pdq-dark' || theme === 'pdq-light') {
      document.documentElement.setAttribute('data-theme', theme);
    }

    this.platform.ready().then(() => {});
  }

  private setupSwUpdates() {
    if (!this.swUpdate.isEnabled) return;

    this.swUpdate.versionUpdates.subscribe((evt) => {
      if (evt.type === 'VERSION_READY') this.applySwUpdateWhenIdle();
    });

    // ngsw only auto-checks for updates on browser navigations; an SPA tab can
    // sit on one URL for hours without triggering one, so nudge the check.
    setInterval(() => this.swUpdate.checkForUpdate().catch(() => {}), 30 * 60_000);
  }

  private applySwUpdateWhenIdle() {
    if (document.visibilityState !== 'visible') {
      this.activateAndReload();
      return;
    }
    const onHidden = () => {
      if (document.visibilityState !== 'hidden') return;
      document.removeEventListener('visibilitychange', onHidden);
      this.activateAndReload();
    };
    document.addEventListener('visibilitychange', onHidden);
  }

  private async activateAndReload() {
    try {
      await this.swUpdate.activateUpdate();
    } catch {
      // Reload anyway — a failed activate is recoverable on the next page load.
    }
    document.location.reload();
  }

  async ngOnInit() {
    try {
      this.showProjectSelector = !this.authService.isNewNavEnabled();
      const appConfigReference = doc(this.afs, collections.APP_CONFIG, 'frontend');
      const appConfig = await getDoc(appConfigReference);

      if (appConfig.exists()) {
        const data = appConfig.data();

        this.gitHash = data.hash;
      }

      onSnapshot(appConfigReference, (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          const gitHash = data.hash;

          if (this.gitHash !== gitHash) {
            // window.location.reload();
            this.showSuggestReloadPrompt();
          }

          this.gitHash = data.hash;
        }
      });
    } catch (err) {
      console.error('Error getting app config: ', err);
    }

    // Start analytics
    this.analyticsService.init(environment.googleAnalyticsTrackingId);
    this.route.queryParams.subscribe((params) => {
      this.hideNavBar = this.shouldHideNavBar(params);
    });
  }

  private shouldHideNavBar(params: Params): boolean {
    // Janky way of seeing if we're redirecting for oauth in the app.unthread.io domain
    return (
      this.embeddedAppConfigService.shouldHideUnthreadTopNav ||
      this.router.routerState.snapshot.url.startsWith('/submit-survey-response') ||
      (!!params.redirectForOAuth && !this.authService.tenantId) ||
      (!!params.tvMode && params.tvMode === 'true')
    );
  }

  async configureForTenant() {
    const primaryColor = this.authService.primaryColor;
    const tenant = this.authService.tenant;

    if (primaryColor) {
      document.body.style.setProperty(`--ion-color-primary`, primaryColor);
      document.body.style.setProperty(`--ion-color-primary-rgb`, hexToRgb(primaryColor));
    }

    if (tenant?.accountStatus === 'trial') {
      if (tenant.trialEndDate) {
        const end = moment(tenant.trialEndDate);
        this.trialExpired = end.diff(moment()) < 0;
        this.trialStatus = this.trialExpired
          ? `Trial expired. Click to upgrade`
          : `Trial expires ${moment(tenant.trialEndDate).fromNow()}`;
      } else {
        this.trialStatus = 'Trial period';
        this.trialExpired = false;
      }
    }

    this.isSuperAdmin = this.authService.isSuperAdmin && _.get(tenant, 'isInternal');
    this.isReady = true;
  }

  async subscribeToNotifications() {
    if (!this.authService.user) {
      return;
    }

    collectionData(
      query(
        collection(this.afs, collections.NOTIFICATIONS),
        where('userId', '==', this.authService.userId),
        where('tenantId', '==', this.authService.tenantId),
        limit(50),
        orderBy('createdAt', 'desc'),
      ),
      { idField: 'objectId' },
    ).subscribe(
      (val) => {
        this.notifications = val;

        let notifs = 0;
        this.notifications.forEach((n) => {
          if (!n.read) {
            notifs++;
          }

          // Auto-download customer exports when notification first arrives
          this.handleAutoDownloadExport(n);
        });
        this.unreadNotifications = notifs;
      },
      (err) => {
        console.log('Error getting notifications: ', err);
      },
    );
  }

  /**
   * Auto-download exports when the notification arrives, but only if
   * the export was initiated in this session.
   */
  private async handleAutoDownloadExport(notification: any) {
    const exportId = notification.data?.exportId;

    if (!exportId) {
      return;
    }

    if (notification.type === USER_NOTIFICATION_TYPES.PEOPLE_EXPORT_COMPLETED) {
      if (!this.usersService.wasExportInitiatedInSession(exportId)) {
        return;
      }

      this.usersService.clearInitiatedExport(exportId);

      try {
        await this.usersService.downloadExport(exportId);
      } catch (error) {
        console.error('Failed to auto-download People export:', error);
      }

      return;
    }

    if (notification.type === USER_NOTIFICATION_TYPES.CUSTOMER_EXPORT_COMPLETED) {
      if (!this.customersService.wasExportInitiatedInSession(exportId)) {
        return;
      }

      this.customersService.clearInitiatedExport(exportId);

      try {
        await this.customersService.downloadExport(exportId);
      } catch (error) {
        console.error('Failed to auto-download export:', error);
      }

      return;
    }

    if (notification.type === USER_NOTIFICATION_TYPES.CONVERSATION_EXPORT_COMPLETED) {
      if (!this.conversationsService.wasExportInitiatedInSession(exportId)) {
        return;
      }

      this.conversationsService.clearInitiatedExport(exportId);

      try {
        await this.conversationsService.downloadExport(exportId);
      } catch (error) {
        console.error('Failed to auto-download export:', error);
      }
    }
  }

  async openProfile() {
    const popover = await this.popoverCtrl.create({
      component: ProfilePopoverPage,
      componentProps: {},
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async openCommandBar() {
    this.analyticsService.trackEvent('Nav', 'Open Custom Command Bar');
    this.customCommandBarService.toggle();
  }

  async openGiftOptions() {
    const options: ActionDropdownType[] = [
      {
        label: 'Product Updates',
        icon: 'rocket-outline',
      },
      {
        label: 'Refer a Friend',
        icon: 'heart-outline',
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (idx: number) => {
          popover.dismiss();

          if (idx === 0) {
            window.open('https://unthread.io/blog/tag/product-updates/', '_blank');
          } else if (idx === 1) {
            this.openReferral();
          }
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async openReferral() {
    this.analyticsService.trackEvent('Nav', 'View Referral');

    const segments = this.authService.tenantId.split('-');
    const code = segments[segments.length - 1];
    const link = `unthread.io/?referral=${code}`;

    const alert = await this.alertCtrl.create({
      header: 'Get free stuff',
      subHeader: `Get a free month of Unthread when you refer a friend and they sign up for a subscription.`,
      inputs: [
        {
          name: 'code',
          value: link,
          placeholder: '',
        },
      ],
      buttons: [
        {
          text: 'Copy Link',
          handler: async () => {
            navigator.clipboard
              .writeText(link)
              .then(() => {
                this.msgSrvc.show('✓ Copied link to clipboard');
              })
              .catch((err) => {
                console.error('Could not copy text', err);
              });
          },
        },
        {
          text: 'Dismiss',
        },
      ],
    });
    alert.present();
  }

  async openNotifications() {
    const popover = await this.popoverCtrl.create({
      component: NotificationsComponent,
      componentProps: {
        notifications: this.notifications,
        unreadCount: this.unreadNotifications,
      },
      showBackdrop: false,
      event,
      cssClass: 'notifications-popover',
    });
    popover.present();
  }

  loginOrSignup() {
    if (this.router.routerState.snapshot.url.startsWith('/login')) {
      return;
    }

    this.navController.navigateRoot(['/login'], {
      queryParams: {
        redirect: this.router.routerState.snapshot.url,
      },
    });
  }

  handlePushAction(action: any) {
    const notif = action.notification;

    if (notif) {
      const planId = _.get(notif, 'data.planId');
      // const type = _.get(notif, 'data.alertType');
      // const guestId = _.get(notif, 'data.guestId');

      if (planId) {
        this.navController.navigateForward(`/events/${planId}`);
      }
    }
  }

  returnToLogin() {
    this.authService.logout();
  }

  get isPasswordSetupPage() {
    return this.router.url.split('?')[0] === '/password-setup';
  }

  async sendPasswordSetupEmail() {
    try {
      await this.authService.sendPasswordSetupEmailForUser(this.authService.user);
      this.msgSrvc.show('✓ Password setup email sent. Choose your password after opening it.');
    } catch {
      this.msgSrvc.show('The setup email could not be sent. Please try again or use another enabled sign-in method.');
    }
  }

  async showCreateOptions() {
    this.globalCreateService.conversation();
  }

  async showSuggestReloadPrompt() {
    if (this.isReloadPromptShowing) {
      return;
    }

    this.isReloadPromptShowing = true;

    const msg = await this.msgSrvc.show('A new version of Unthread is available', {
      duration: 0,
      position: 'bottom',
      buttons: [
        {
          text: 'Reload Now',
          handler: () => {
            window.location.reload();
          },
        },
        {
          text: 'Dismiss',
          role: 'cancel',
          handler: () => {
            msg.dismiss();
          },
        },
      ],
    });

    msg.onDidDismiss().then(() => {
      this.isReloadPromptShowing = false;
    });
  }

  async showProjectOptions() {
    const popover = await this.popoverCtrl.create({
      component: ProjectSelectorComponent,
      showBackdrop: false,
      event,
    });

    await popover.present();
  }

  async showAccountOptions() {
    // Show an action popover with the option to view project settings
    const options: ActionDropdownType[] = [
      {
        label: 'Workspace Settings',
        icon: 'settings-outline',
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (_index: number, label: string) => {
          if (label === 'Workspace Settings') {
            this.navController.navigateRoot('/dashboard/settings');
          }
        },
      },
      showBackdrop: false,
      event,
    });
    await popover.present();
  }

  navigateToHome() {
    // For new nav, navigate to the user's defaultInboxView or inbox/me
    // For old nav, navigate to the dashboard home
    if (this.authService.isNewNavEnabled()) {
      const defaultInboxView = this.authService.profile?.defaultInboxView;

      if (defaultInboxView) {
        this.navController.navigateRoot(defaultInboxView);
      } else {
        this.navController.navigateRoot('/dashboard/inbox/me');
      }
    } else {
      this.navController.navigateRoot('/dashboard');
    }
  }

  async showQuickComposeOptions() {
    const COMPOSE_EMAIL = 'Email';
    const COMPOSE_SLACK = 'Slack message';

    const options: ActionDropdownType[] = [
      {
        label: COMPOSE_SLACK,
        icon: 'logo-slack',
      },
      {
        label: COMPOSE_EMAIL,
        icon: 'mail-outline',
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (_index: number, label: string) => {
          popover.dismiss();

          if (label === COMPOSE_EMAIL) {
            this.globalCreateService.composeEmail();
          } else if (label === COMPOSE_SLACK) {
            this.globalCreateService.composeSlack();
          }
        },
      },
      showBackdrop: false,
      event,
    });

    popover.present();
  }
}
